import React from "react";
import { Link } from "react-router-dom";
import { cn } from "./cn";
import { CompanyMark } from "./CompanyMark";
import { CheckIcon, GridIcon, PlusIcon, StarIcon } from "../../../assets/ui/icons";

/* The account menu panel. Transcribed from the design export's ProfileMenu (react-export src/components/chrome.jsx): a "Signed in as" header, the switcher as... */

/* The switcher's second line, from the profile_status ladder the API already sends (verified → member → claimed → listed). */
export const STATUS_LINE = {
  verified: { label: "Verified", member: true, dot: "bg-[var(--success)]" },
  member: { label: "Claimed", member: true, dot: "bg-[var(--gold-star)]" },
  claimed: { label: "Claimed", member: false, dot: "bg-[var(--navy)]" },
  listed: { label: "Listed", member: false, dot: "bg-[var(--muted-2)]" },
};

export const statusLine = (profileStatus) => STATUS_LINE[profileStatus] || STATUS_LINE.listed;

/* One action on the menu's foot. */
export const MenuRow = ({ icon, children, danger = false, as: As = "button", ...rest }) => (
  <As
    role="menuitem"
    {...(As === "button" ? { type: "button" } : {})}
    className={cn(
      "flex w-full items-center gap-2.5 rounded-[9px] px-2.5 py-[9px]",
      "text-left text-[13.5px] font-semibold font-sans transition-colors",
      danger
        ? "text-[var(--coral)] hover:bg-white"
        : "text-[var(--ink-2)] hover:bg-white hover:text-[var(--ink)]"
    )}
    {...rest}
  >
    <span className={cn("flex shrink-0", danger ? "text-[var(--coral)]" : "text-[var(--muted)]")}>
      {icon}
    </span>
    {children}
  </As>
);

const LogOutIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
    <path d="M6 14H3.5A1.5 1.5 0 012 12.5v-9A1.5 1.5 0 013.5 2H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M10.5 11L14 8l-3.5-3M14 8H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* One company in the switcher. */
export const CompanyRow = ({
  company,
  active = false,
  isDefault = false,
  onSelect = () => {},
  onSetDefault = () => {},
}) => {
  const s = statusLine(company?.profile_status);

  return (
    <li className="list-none">
      <div
        className={cn(
          "group flex items-center gap-1 rounded-[10px] transition-colors",
          active ? "bg-[var(--coral-soft)]" : "hover:bg-[var(--slate-2)]"
        )}
      >
        <button
          type="button"
          role="menuitem"
          aria-label={company?.name}
          aria-current={active ? "true" : undefined}
          onClick={() => onSelect(company)}
          className="flex min-w-0 flex-1 items-center gap-[11px] rounded-[10px] px-2.5 py-[9px] text-left"
        >
          <CompanyMark company={company} size="xs" member={s.member} tone="brand" />

          <span className="min-w-0 flex-1 overflow-hidden">
            <span
              title={company?.name}
              className="block truncate text-[13.5px] font-semibold text-[var(--ink)] font-sans"
            >
              {company?.name}
            </span>
            <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-[var(--muted)] font-sans">
              <i aria-hidden="true" className={cn("h-[7px] w-[7px] shrink-0 rounded-full", s.dot)} />
              {s.label}
              {s.member ? " · Member" : ""}
            </span>
          </span>

          <span
            className={cn("flex shrink-0 text-[var(--coral)]", active ? "opacity-100" : "opacity-0")}
            aria-hidden="true"
          >
            <CheckIcon size={15} stroke="currentColor" />
          </span>
        </button>

        {/* TIP-499 — which company you land on next time. Marked when set;
            a button to set it otherwise. */}
        {isDefault ? (
          <span
            title="Default business"
            aria-label="Default business"
            className="mr-1.5 flex h-7 w-7 shrink-0 items-center justify-center text-[var(--gold-star)]"
          >
            <StarIcon size={15} />
          </span>
        ) : (
          <button
            type="button"
            title="Set as default"
            aria-label={`Set ${company?.name} as default`}
            onClick={() => onSetDefault(company)}
            className="mr-1.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[var(--muted-2)] transition-colors hover:bg-[var(--gold-soft)] hover:text-[var(--gold-star)]"
          >
            <StarIcon size={15} />
          </button>
        )}
      </div>
    </li>
  );
};

export const ProfileMenu = ({
  name,
  email,
  companies = [],
  activeCompanyId,
  defaultCompanyId,
  onSelectCompany = () => {},
  onSetDefault = () => {},
  onProfile,
  onLogout = () => {},
  dashboardTo = "/dashboard",
  /* The claim intake — the flow that finds your business in the directory and walks the five steps. */
  listBusinessTo = "/sign-up",
  listBusinessState = { redirectToStep: 6 },
  className = "",
}) => {
  /* The default is pinned to the top; everything else keeps the order it came
     in (sort is stable, so equal-rank rows do not shuffle). Transcribed from
     CompanySwitcherList. */
  const ordered = defaultCompanyId
    ? [...companies].sort((a, b) => {
        if (a.companyID === defaultCompanyId) return -1;
        if (b.companyID === defaultCompanyId) return 1;
        return 0;
      })
    : companies;

  return (
    <div
      role="menu"
      aria-label="Account menu"
      className={cn(
        "u1-root absolute right-0 top-full z-50 mt-3 flex w-[330px] max-w-[82vw] flex-col",
        "overflow-hidden rounded-[14px] border border-[var(--line)] bg-white",
        "shadow-[shadow:var(--menu-shadow)]",
        /* A dropdown hanging off a sticky bar runs past a phone's fold with no
           way to reach its last rows, so below the phone breakpoint it becomes
           a bottom sheet instead. */
        /* No `right-auto` here: it would cancel inset-x-0's right edge and
           let the sheet size to its content, overflowing the viewport. */
        "max-[743px]:fixed max-[743px]:inset-x-0 max-[743px]:bottom-[var(--tabbar-clear)]",
        "max-[743px]:top-auto max-[743px]:z-[240] max-[743px]:mt-0 max-[743px]:w-auto",
        "max-[743px]:max-w-none max-[743px]:max-h-[84dvh] max-[743px]:overflow-y-auto",
        "max-[743px]:rounded-b-none max-[743px]:rounded-t-[18px]",
        "max-[743px]:shadow-[shadow:var(--sheet-shadow)]",
        className
      )}
    >
      {/* Signed in as — a button, because it was one before: it opens the
          profile. */}
      <button
        type="button"
        role="menuitem"
        onClick={onProfile}
        className="w-full border-b border-[var(--line)] px-4 pb-3 pt-3.5 text-left transition-colors hover:bg-[var(--slate-2)]"
      >
        <span className="block text-[10.5px] font-bold uppercase tracking-[.12em] text-[var(--muted)] font-sans">
          Signed in as
        </span>
        <span className="mt-1.5 block truncate text-[14.5px] font-bold text-[var(--ink)] font-sans">
          {name}
        </span>
        <span className="mt-px block truncate text-[12.5px] text-[var(--muted)] font-sans">
          {email}
        </span>
      </button>

      {/* The switcher. Capped and scrolling: an account on twenty businesses
          would otherwise push the actions off the bottom of the screen. */}
      {ordered.length > 0 && (
        <ul className="m-0 max-h-[290px] overflow-y-auto overflow-x-hidden p-2 max-[743px]:max-h-[52dvh]">
          {ordered.map((c) => (
            <CompanyRow
              key={c.companyID}
              company={c}
              active={c.companyID === activeCompanyId}
              isDefault={c.companyID === defaultCompanyId}
              onSelect={onSelectCompany}
              onSetDefault={onSetDefault}
            />
          ))}
        </ul>
      )}

      <div className="shrink-0 border-t border-[#e5dfd2] bg-[#f5f1e8] p-2">
        <MenuRow as={Link} to={dashboardTo} icon={<GridIcon size={16} />}>
          Dashboard
        </MenuRow>
        {/* Adding a company is a thing you do FROM the switcher — the menu
            already lists the ones you have, so the way to get another belongs
            beside them rather than only in the public navbar. */}
        <MenuRow
          as={Link}
          to={listBusinessTo}
          state={listBusinessState}
          icon={<PlusIcon size={15} />}
        >
          List another business
        </MenuRow>
        <MenuRow onClick={onLogout} icon={<LogOutIcon />} danger>
          Log out
        </MenuRow>
      </div>
    </div>
  );
};

export default ProfileMenu;
